import { JSX, RefObject } from 'react';

import {
    AppStateStatus,
    ColorValue,
    LayoutChangeEvent,
    StyleProp,
    ViewStyle,
} from 'react-native';

export enum EPGAlignment {
    start,
    center,
    end,
}

export enum EPGRequestPriority {
    high = 3, // For visible loading cells
    normal = 2, // For engaged loading cells
    low = 1, // For pre-loading, when EPG is Idle
}

// ---------- CONFIG ----------

export type EPGConfig = {
    paddingColor: ColorValue;
    skeletonCellsColor: ColorValue;
    padding: number;
    cellBorderRadius: number;
    channelWidth: number;
    timebarHeight: number;
    rowHeight: number;
    hoursCount: number;
    hourWidth: number;
    markerToLineAlignment: EPGAlignment;
    stickyMarker: boolean;
    stickyMarkerPadding: number;
    stickyPrograms: boolean;
    stickyProgramTriggerMins: number;
    stickyProgramReleaseMins: number;
    renderAheadOffsetHorizontal: number;
    renderAheadOffsetVertical: number;
    tvHorizontalScrollAmount: number;
    needDataDelay: number;
    preloadEnabled: boolean;
    idlePeriod: number;
    idleRequestCount: number;
    decelerationRate: number;
};

export type EPGConfigChangedCallback = (config: EPGConfig) => void;

export type EPGConfiChangedSubscription = {
    remove: () => void;
};

// ---------- CHANNELS ----------

export type EPGChannel<T> = {
    id: string;
    name: string;
    number: number;
    extra?: T;
};

export type EPGChannels<T> = EPGChannel<T>[];

// ---------- PROGRAMS ----------

export type EPGProgram<T> = {
    id: string;
    channelId: string;
    title: string;
    startTime: number;
    endTime: number;
    extra?: {
        propsAreEqual?(
            prevProgram: EPGProgram<T> | undefined,
            nextProgram: EPGProgram<T> | undefined,
        ): boolean;
    } & T;
};

export type EPGPrograms<T> = {
    // Empty array means no data available for the channel,
    // undefined means not fetched (aka loading cell)
    [channelId: string]: EPGProgram<T>[] | undefined;
};

// ---------- PROPS ----------

export type EPGProps<C, P> = {
    style?: StyleProp<ViewStyle>;
    // TODO: temporary prop, will use EPGConfigManager later
    // now this prop should never change (beause of LayoutProvider)
    config: Partial<EPGConfig>;
    channels: EPGChannels<C>;
    programs: EPGPrograms<P>;
    startTime: number;
    horizontalScrollEnabled?: boolean;
    verticalScrollEnabled?: boolean;
    isActive?: boolean;
    isGridFocusedRef?: RefObject<boolean>;

    // Callbacks
    onLayout?: (e: LayoutChangeEvent) => void;
    onVerticalScroll?: (offsetY: number) => void;
    onHorizontalScroll?: (offsetX: number) => void;
    onProgramSelected?: (program: EPGProgram<P>) => void;
    onProgramPressTV?: (program: EPGProgram<P>) => void;
    onChannelPress?: (channel: EPGChannel<C>) => void;
    onChannelAccessibilityAction?: (channel: EPGChannel<C>) => void;
    onNowMarkerPress?: () => void;
    needProgramsForChannels?: (
        channels: EPGChannels<C>,
        priority: EPGRequestPriority,
    ) => boolean;

    // Renderers
    renderTopLeftAccessoryView?: () => JSX.Element;
    renderChannelCell: (channel: EPGChannel<any>) => JSX.Element;
    renderHourCell: (time: number) => JSX.Element;
    renderProgramCell: (
        program: EPGProgram<any>,
        rowIndex: number,
        selected: boolean,
        live: boolean,
    ) => JSX.Element;
    renderMissingDataCell: (
        rowIndex: number,
        selected: boolean,
        live: boolean,
    ) => JSX.Element;
    renderLoadingRow: (rowIndex: number, selected: boolean) => JSX.Element;
    renderNoDataRow: (rowIndex: number, selected: boolean) => JSX.Element;
    renderNowLine?: () => JSX.Element;
    renderNowMarker?: (onLayout: (e: LayoutChangeEvent) => void) => JSX.Element;
    screenReaderEnabled: boolean;
    appState?: AppStateStatus;
};

// ---------- INTERFACE ----------

export interface IEPG {
    restoreScrollPosition: () => void;

    scrollByTime: (time: number, animated?: boolean) => void;
    scrollToTime: (
        time: number,
        alignment?: EPGAlignment,
        animated?: boolean,
    ) => void;
    scrollToNow: (alignment?: EPGAlignment, animated?: boolean) => void;

    scrollToChannel: (
        channelId: string,
        alignment?: EPGAlignment,
        animated?: boolean,
    ) => void;

    selectProgram: (channelId: string, programId: string) => void;

    scrollToYOffset: (yOffset: number) => void;
}
